import { useState, useCallback, useEffect, useRef } from 'react'

// Architecture: see ADR-081 — vault/12-technical/ADR/ADR-081-messages-polling-disabled-latest-ref-pattern.md

export interface Message {
  id: string
  threadId: string
  senderId: string
  senderRole: 'STAFF' | 'MERCHANT' | 'BUYER'
  body: string
  readAt: string | null
  createdAt: string
}

export interface Thread {
  id: string
  merchantId: string
  accountId: string | null
  staffId: string | null
  label: 'CUSTOMER' | 'SUPPORT' | 'COMPLIANCE' | 'GENERAL'
  pinnedAt: string | null
  messages: Message[]
  createdAt: string
  updatedAt: string
}

export interface UseMessagesOptions {
  apiBase: string
  getAuthHeader: () => string | null
  enabled?: boolean
  refetchInterval?: number // ms, default 10000 (10s polling fallback)
}

export interface UseMessagesReturn {
  threads: Thread[]
  unreadThreadCount: number
  loading: boolean
  activeThread: Thread | null
  setActiveThread: (thread: Thread | null) => void
  sendMessage: (threadId: string, body: string) => Promise<void>
  markThreadRead: (threadId: string) => Promise<void>
  pinThread: (threadId: string, pinned: boolean) => Promise<void>
  createThread: (merchantId: string, label?: Thread['label']) => Promise<Thread>
  refetch: () => void
}

export function useMessages(options: UseMessagesOptions): UseMessagesReturn {
  const { apiBase, getAuthHeader, enabled = true, refetchInterval = 10000 } = options
  const [threads, setThreads] = useState<Thread[]>([])
  const [loading, setLoading] = useState(false)
  const [activeThread, setActiveThread] = useState<Thread | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeRef = useRef(true)

  // Latest-ref pattern (same as useNotifications' getAuthHeaderRef): consumers
  // pass getAuthHeader as an inline arrow — a new reference every render. With
  // getAuthHeader in buildHeaders' deps, every render rebuilt buildHeaders →
  // fetchThreads → re-ran the polling effect, whose first line fires an
  // IMMEDIATE fetch — and that fetch's setState re-renders the consumer,
  // closing the loop. Result: GET /api/threads at network round-trip speed
  // (~5/sec) from any mounted consumer with enabled: true (2026-07-11,
  // admin-portal MessagesPage hammering the staging API). The ref keeps
  // buildHeaders stable across renders while always calling the latest fn.
  const getAuthHeaderRef = useRef(getAuthHeader)
  useEffect(() => { getAuthHeaderRef.current = getAuthHeader }, [getAuthHeader])

  const buildHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const auth = getAuthHeaderRef.current()
    if (auth) headers['Authorization'] = auth
    return headers
  }, [])

  const unreadThreadCount = threads.filter(t => t.messages.some(m => !m.readAt)).length

  const fetchThreads = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true
    setLoading(true)
    try {
      const res = await fetch(`${apiBase}/api/threads`, {
        credentials: 'include',
        headers: buildHeaders(),
      })
      if (!res.ok) return false
      const data = (await res.json()) as { threads: Thread[] }
      setThreads(data.threads)
      return true
    } catch (error: unknown) {
      console.error('[useMessages] failed to fetch threads', error)
      return false
    } finally {
      setLoading(false)
    }
  }, [apiBase, enabled, buildHeaders])

  // Polling fallback (WebSocket deferred to Phase 9) — exponential backoff on
  // failure so a sustained network outage doesn't hammer the API at a fixed
  // interval forever; resets to refetchInterval as soon as a poll succeeds.
  useEffect(() => {
    if (!enabled) return
    activeRef.current = true
    let retryDelay = refetchInterval

    async function poll() {
      if (!activeRef.current) return
      const ok = await fetchThreads()
      if (!activeRef.current) return
      retryDelay = ok ? refetchInterval : Math.min(retryDelay * 2, 60000)
      timeoutRef.current = setTimeout(() => void poll(), retryDelay)
    }

    void poll()

    return () => {
      activeRef.current = false
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [enabled, apiBase, refetchInterval, fetchThreads])

  const sendMessage = useCallback(
    async (threadId: string, body: string) => {
      await fetch(`${apiBase}/api/threads/${threadId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: buildHeaders(),
        body: JSON.stringify({ body }),
      })
      await fetchThreads()
    },
    [apiBase, fetchThreads, buildHeaders]
  )

  const markThreadRead = useCallback(
    async (threadId: string) => {
      await fetch(`${apiBase}/api/threads/${threadId}/read`, {
        method: 'PATCH',
        credentials: 'include',
        headers: buildHeaders(),
      })
      setThreads(prev =>
        prev.map(t =>
          t.id === threadId
            ? {
                ...t,
                messages: t.messages.map(m => ({
                  ...m,
                  readAt: m.readAt ?? new Date().toISOString(),
                })),
              }
            : t
        )
      )
    },
    [apiBase, buildHeaders]
  )

  const pinThread = useCallback(
    async (threadId: string, pinned: boolean) => {
      await fetch(`${apiBase}/api/threads/${threadId}/pin`, {
        method: 'PATCH',
        credentials: 'include',
        headers: buildHeaders(),
        body: JSON.stringify({ pinned }),
      })
      setThreads(prev =>
        prev.map(t =>
          t.id === threadId
            ? { ...t, pinnedAt: pinned ? new Date().toISOString() : null }
            : t
        )
      )
    },
    [apiBase, buildHeaders]
  )

  const createThread = useCallback(
    async (merchantId: string, label: Thread['label'] = 'GENERAL'): Promise<Thread> => {
      const res = await fetch(`${apiBase}/api/threads`, {
        method: 'POST',
        credentials: 'include',
        headers: buildHeaders(),
        body: JSON.stringify({ merchantId, label }),
      })
      if (!res.ok) throw new Error('Failed to create thread')
      const data = (await res.json()) as { thread: Thread }
      await fetchThreads()
      return data.thread
    },
    [apiBase, fetchThreads, buildHeaders]
  )

  // Refresh active thread when threads list updates
  useEffect(() => {
    if (!activeThread) return
    const updated = threads.find(t => t.id === activeThread.id)
    if (updated) setActiveThread(updated)
  }, [threads, activeThread?.id])

  return {
    threads,
    unreadThreadCount,
    loading,
    activeThread,
    setActiveThread,
    sendMessage,
    markThreadRead,
    pinThread,
    createThread,
    refetch: fetchThreads,
  }
}
